// packages/ui/src/components/security/SecurityCenterPage.tsx
//
// Wave 12 Task 104 — shared Security Center dashboard.
// Reference: design PNG `merchant-security-2fa-setup` (mislabeled — content
// is actually the Security Center dashboard, not 2FA setup).
//
// Composes 3 sub-blocks:
//   - SecurityScoreCard (left column, "85" + Healthy + green progress bar)
//   - SecurityStatsRow (4 stat cards: Active Sessions / Failed Attempts /
//     Blocked Attempts / API Keys Active)
//   - 3-tab block: ACTIVITY LOG / ACTIVE SESSIONS / SECURITY SETTINGS
//     (Activity Log = SecurityActivityLog table; other tabs = placeholders)
//
// Page-level wrapper sits in 3 SPA thin wrappers (apps/{admin,merchant,
// vendor}/src/pages/settings/SecurityCenterPage.tsx) which add SettingsLayout
// portal + activeTab="security" around this component.

import { useEffect, useState, type JSX } from 'react';
import { Button } from '@printway-global/design-system';
import { cn } from '@printway-global/design-system';
import { SecurityScoreCard } from '@printway-global/design-system';
import { SecurityStatsRow } from './SecurityStatsRow.js';
import { SecurityActivityLog } from './SecurityActivityLog.js';
import { ActiveSessionsTab } from './ActiveSessionsTab.js';
import { SecuritySettingsTab } from './SecuritySettingsTab.js';

// R3-2 (2026-05-16): SPA host has no /api proxy on sandbox/prod — relative
// /api fetches returned index.html so JSON parse silently failed and the
// page kept its mock data (John Doe rows). Same fix as ActiveSessionsTab.
const _API_BASE = (
  (
    (import.meta as unknown as { env?: { VITE_API_BASE_URL?: string } }).env
      ?.VITE_API_BASE_URL ?? ''
  ) as string
).replace(/\/$/, '');

function apiUrl(path: string): string {
  if (!path.startsWith('/api/')) return path;
  return _API_BASE ? `${_API_BASE}${path}` : path;
}

export interface SecurityScore {
  score: number;
  status: 'healthy' | 'warning' | 'critical';
}

export interface SecurityStats {
  activeSessions: number;
  failedAttempts: number;
  blockedAttempts: number;
  apiKeysActive: number;
}

export interface SecurityActivityEntry {
  timestamp: string;
  user: string;
  action: string;
  ipAddress: string;
  status: 'success' | 'failed' | 'blocked';
}

export interface SecurityCenterPageProps {
  /** Override the score data (else mock fallback). */
  scoreOverride?: SecurityScore;
  /** Override the stats (else mock fallback). */
  statsOverride?: SecurityStats;
  /** Override activity entries (else mock fallback). */
  activityOverride?: SecurityActivityEntry[];
  /** Wave 12 scope: data fetched from these endpoints when no override. */
  scoreUrl?: string;
  statsUrl?: string;
  activityUrl?: string;
  /** Click handler for "Run Security Scan" button (default: toast placeholder). */
  onRunScan?: () => void;
}

const MOCK_SCORE: SecurityScore = { score: 85, status: 'healthy' };
const MOCK_STATS: SecurityStats = {
  activeSessions: 3,
  failedAttempts: 1,
  blockedAttempts: 0,
  apiKeysActive: 3,
};
// Empty mock — relies on BE call to populate. R3-2 (2026-05-16): removed
// the John Doe seed rows. If the BE fetch fails the table renders empty
// instead of pretending another user logged in.
const MOCK_ACTIVITY: SecurityActivityEntry[] = [];

type TabId = 'activity' | 'sessions' | 'settings';

const TABS: ReadonlyArray<{ id: TabId; label: string }> = [
  { id: 'activity', label: 'ACTIVITY LOG' },
  { id: 'sessions', label: 'ACTIVE SESSIONS' },
  { id: 'settings', label: 'SECURITY SETTINGS' },
];

export function SecurityCenterPage({
  scoreOverride,
  statsOverride,
  activityOverride,
  scoreUrl = '/api/v1/auth/security/score',
  statsUrl = '/api/v1/auth/security/stats',
  activityUrl = '/api/v1/auth/security/activity-log?limit=20',
  onRunScan,
}: SecurityCenterPageProps): JSX.Element {
  const [score, setScore] = useState<SecurityScore>(scoreOverride ?? MOCK_SCORE);
  const [stats, setStats] = useState<SecurityStats>(statsOverride ?? MOCK_STATS);
  const [activity, setActivity] = useState<SecurityActivityEntry[]>(
    activityOverride ?? MOCK_ACTIVITY,
  );
  const [activeTab, setActiveTab] = useState<TabId>('activity');
  const [scanState, setScanState] = useState<'idle' | 'running' | 'done'>('idle');
  const [scanFindings, setScanFindings] = useState<
    Array<{ id: string; level: 'ok' | 'warn'; label: string }>
  >([]);

  // Phase 1: try fetching real BE data; fall back to mock on error.
  useEffect(() => {
    if (scoreOverride) return;
    void fetch(apiUrl(scoreUrl), { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((j: { success?: boolean; data?: SecurityScore } | null) => {
        if (j?.success && j.data) setScore(j.data);
      })
      .catch(() => {
        /* keep mock */
      });
  }, [scoreOverride, scoreUrl]);

  useEffect(() => {
    if (statsOverride) return;
    void fetch(apiUrl(statsUrl), { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((j: { success?: boolean; data?: SecurityStats } | null) => {
        if (j?.success && j.data) setStats(j.data);
      })
      .catch(() => {
        /* keep mock */
      });
  }, [statsOverride, statsUrl]);

  useEffect(() => {
    if (activityOverride) return;
    void fetch(apiUrl(activityUrl), { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((j: { success?: boolean; data?: SecurityActivityEntry[] } | null) => {
        if (j?.success && Array.isArray(j.data)) setActivity(j.data);
      })
      .catch(() => {
        /* keep mock */
      });
  }, [activityOverride, activityUrl]);

  const handleRunScan = (): void => {
    if (onRunScan) {
      onRunScan();
      return;
    }
    // UAT-3 (2026-05-16): synthesise an inline scan result from the stats
    // we already fetched. No BE endpoint exists yet, so the value is in
    // surfacing visible findings — a 1.2s spinner with no output read as
    // "doesn't work" during UAT.
    setScanState('running');
    setScanFindings([]);
    setTimeout(() => {
      const findings: Array<{ id: string; level: 'ok' | 'warn'; label: string }> = [];
      findings.push({
        id: 'sessions',
        level: stats.activeSessions > 5 ? 'warn' : 'ok',
        label:
          stats.activeSessions > 5
            ? `${stats.activeSessions} phiên đang hoạt động — kiểm tra tab Active Sessions.`
            : `${stats.activeSessions} phiên đang hoạt động — bình thường.`,
      });
      findings.push({
        id: 'failed',
        level: stats.failedAttempts > 3 ? 'warn' : 'ok',
        label:
          stats.failedAttempts > 3
            ? `${stats.failedAttempts} lần đăng nhập thất bại trong 24h — bật cảnh báo qua email nếu chưa.`
            : `${stats.failedAttempts} lần đăng nhập thất bại trong 24h — ổn.`,
      });
      findings.push({
        id: 'blocked',
        level: stats.blockedAttempts > 0 ? 'warn' : 'ok',
        label:
          stats.blockedAttempts > 0
            ? `${stats.blockedAttempts} truy cập đã bị chặn — xem log để biết IP.`
            : 'Chưa có truy cập nào bị chặn.',
      });
      findings.push({
        id: 'score',
        level: score.score < 70 ? 'warn' : 'ok',
        label:
          score.score < 70
            ? `Điểm bảo mật ${score.score}/100 thấp — bật 2FA + đổi mật khẩu gần đây.`
            : `Điểm bảo mật ${score.score}/100 — ổn định.`,
      });
      setScanFindings(findings);
      setScanState('done');
    }, 1400);
  };

  return (
    <div className="space-y-6">
      {/* Page header */}
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-pw-text">
            Security Center
          </h1>
          <p className="mt-1 text-sm text-pw-text-muted">
            Monitor and manage your account security.
          </p>
        </div>
        <div className="flex flex-col items-end gap-1">
          <Button
            type="button"
            onClick={handleRunScan}
            disabled={scanState === 'running'}
            data-testid="security-run-scan"
          >
            {scanState === 'running' ? 'Đang quét…' : 'Run Security Scan'}
          </Button>
          {scanState === 'running' ? (
            <span className="text-xs text-pw-text-muted">
              Đang khởi tạo scan…
            </span>
          ) : null}
        </div>
      </header>

      <SecurityScoreCard score={score.score} status={score.status} />

      <SecurityStatsRow stats={stats} />

      {scanState === 'done' && scanFindings.length > 0 ? (
        <section
          data-testid="security-scan-result"
          aria-live="polite"
          className="space-y-2 rounded-xl border border-pw-border bg-pw-bg p-4 shadow-sm"
        >
          <header className="flex items-center justify-between">
            <h2 className="text-sm font-semibold text-pw-text">
              Kết quả quét bảo mật
            </h2>
            <button
              type="button"
              onClick={() => setScanState('idle')}
              className="text-xs text-pw-text-muted hover:text-pw-text"
              data-testid="security-scan-dismiss"
            >
              Đóng
            </button>
          </header>
          <ul className="space-y-1">
            {scanFindings.map((f) => (
              <li
                key={f.id}
                className={cn(
                  'flex items-start gap-2 rounded-md px-3 py-2 text-sm',
                  f.level === 'warn'
                    ? 'bg-yellow-50 text-yellow-800'
                    : 'bg-green-50 text-green-800',
                )}
              >
                <span aria-hidden="true">
                  {f.level === 'warn' ? '⚠' : '✓'}
                </span>
                <span>{f.label}</span>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {/* Tabs */}
      <div className="rounded-xl border border-pw-border bg-pw-bg shadow-sm">
        <nav role="tablist" aria-label="Security sections" className="flex border-b border-pw-border">
          {TABS.map((t) => {
            const isActive = activeTab === t.id;
            return (
              <button
                key={t.id}
                type="button"
                role="tab"
                aria-selected={isActive}
                onClick={() => setActiveTab(t.id)}
                className={cn(
                  'flex-1 border-b-2 px-4 py-3 text-xs font-bold uppercase tracking-wider transition-colors',
                  isActive
                    ? 'border-pw-primary text-pw-primary-strong'
                    : 'border-transparent text-pw-text-muted hover:text-pw-text',
                )}
              >
                {t.label}
              </button>
            );
          })}
        </nav>

        <div role="tabpanel" className="p-6">
          {activeTab === 'activity' ? (
            <SecurityActivityLog entries={activity} />
          ) : null}
          {activeTab === 'sessions' ? <ActiveSessionsTab /> : null}
          {activeTab === 'settings' ? <SecuritySettingsTab /> : null}
        </div>
      </div>
    </div>
  );
}

export default SecurityCenterPage;
